import { registerAdapter } from '@symbo.ls/fetch'
import { createSupabaseClient } from './supabaseFetchAdapter.js'

// The framework removed its builtin supabase adapter from `@symbo.ls/fetch`
// (smbls@799bf2224 — the PLATFORM has no Supabase), but CUSTOMER projects
// with `db: { adapter: 'supabase' }` (their own Supabase backends) remain a
// product feature. The adapter now lives in ./supabaseFetchAdapter.js and
// is registered into the fetch plugin's cross-copy CUSTOM_ADAPTERS registry
// here. Module-scope registration covers every editor surface that loads
// customer projects (canvas navigation/applyRemoteOps, preview
// renderProject, iframeApp) because they all import this file — and it is
// ALSO re-asserted inside attachDbAdapters()/resolveDbAdapters() because
// Parcel has been seen tree-shaking trailing module-side-effect statements
// from shared files despite `"sideEffects": true` (see the bootShell.js
// eager `registerAdapter('sdk', …)` note). registerAdapter is an idempotent
// map write, so the repeat is free.
//
// FA204 / frank trap — `registerSupabaseAdapter`/`attachOne`/`resolveOne`/
// `ADAPTER_FACTORIES` used to live at module scope and get referenced bare
// from the two exports below; each of the 3 call sites (module-load side
// effect + the two exports) now carries its own self-contained copy —
// registerAdapter is an idempotent map write, so the duplication is free.
registerAdapter('supabase', () => import('./supabaseFetchAdapter.js'))

/**
 * Attach runtime `createClient` factories onto `project.db` and
 * `project.fetch`. Idempotent — safe to call more than once on the same
 * project; sentinel flag prevents re-assignment. Mutates in place and
 * returns `project` for chaining.
 *
 * @param {object} project — loaded project data (from getProjectData).
 * @returns {object} same project, with adapters wired.
 */
export function attachDbAdapters(project) {
  registerAdapter('supabase', () => import('./supabaseFetchAdapter.js'))
  if (!project) return project

  // Extend this map when the project runtime grows a new data backend —
  // adding an entry is the single change required; every editor surface
  // picks it up automatically because they all call `attachDbAdapters`.
  const adapterFactories = Object.freeze({
    supabase: createSupabaseClient
  })
  const attachOne = (cfg) => {
    if (!cfg || cfg.__adapterAttached) return
    const factory = adapterFactories[cfg.adapter]
    // Incomplete configs (missing adapter or url) are left bare — the fetch
    // plugin's adapter resolver will surface the missing field.
    if (!factory || !cfg.url) return
    cfg.createClient = factory
    cfg.__adapterAttached = true
  }

  attachOne(project.db)
  attachOne(project.fetch)
  return project
}

/**
 * Formerly pre-resolved `project.db` / `project.fetch` into adapters in the
 * PARENT realm for a same-origin iframe app. That path is gone: project code
 * runs only in the opaque user-code frame, and the frame's db calls are served
 * by the HOST's own adapter (userCodeFrame.js buildHostSupabaseAdapter, built
 * from the plain url + key). Kept as a registered name that does nothing
 * (review N4: fail closed).
 *
 * @param {object} project
 * @returns {Promise<object>} the same project, untouched.
 */
export async function resolveDbAdapters(project) {
  // USER-CODE-ORIGIN review N4: FAILS CLOSED — a no-op. resolveDb calls the
  // config's `createClient`, and a project payload can carry its own (with
  // `__adapterAttached` so nothing replaces it): parent-realm code with the
  // session in reach. No live caller remains — mountUserCode builds its OWN
  // host adapter from the plain url + key (userCodeFrame.js
  // buildHostSupabaseAdapter) — so nothing here runs.
  return project
}

